import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

const ENTITY_PATH = /^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/;

export function parseCatalogEntityPath(
  pathname: string,
): { entityRef: string; kind: string } | null {
  const match = pathname.match(ENTITY_PATH);
  if (!match) return null;
  const [, namespace, kind, name] = match;
  const k = kind.toLowerCase();
  return { entityRef: `${k}:${namespace}/${name}`, kind: k };
}

export function gatesViewSourceForKind(kind: string): boolean {
  const k = kind.toLowerCase();
  return (
    k === 'component' ||
    k === 'template' ||
    k === 'api' ||
    k === 'group'
  );
}

export type CatalogEntityPagePermissionsValue = {
  /** True when URL is `/catalog/:ns/:kind/:name` (entity layout). */
  onEntityPage: boolean;
  entityRef: string | null;
  kind: string | null;
  /** Whether view-source policy applies to this entity kind. */
  gatesViewSource: boolean;
  /** Same as GET /api/catalog-edit-check?check=edit */
  editAllowed: boolean | null;
  /** Same as GET /api/catalog-edit-check?check=view-source when gatesViewSource; else treated as true. */
  viewSourceAllowed: boolean | null;
};

const defaultValue: CatalogEntityPagePermissionsValue = {
  onEntityPage: false,
  entityRef: null,
  kind: null,
  gatesViewSource: false,
  editAllowed: null,
  viewSourceAllowed: null,
};

const CatalogEntityPagePermissionsContext =
  createContext<CatalogEntityPagePermissionsValue>(defaultValue);

export function useCatalogEntityPagePermissions(): CatalogEntityPagePermissionsValue {
  return useContext(CatalogEntityPagePermissionsContext);
}

/**
 * Fetches catalog-edit-check for the current catalog entity URL and exposes results
 * to descendants (About card, etc.). Replaces body data-* attributes + global CSS.
 */
export function CatalogEntityPagePermissionsProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const location = useLocation();
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const [editAllowed, setEditAllowed] = useState<boolean | null>(null);
  const [viewSourceAllowed, setViewSourceAllowed] = useState<boolean | null>(
    null,
  );

  const pathname =
    location.pathname ||
    (typeof window !== 'undefined' ? window.location.pathname : '');

  const parsed = useMemo(() => parseCatalogEntityPath(pathname), [pathname]);
  const gatesViewSource = parsed
    ? gatesViewSourceForKind(parsed.kind)
    : false;

  useEffect(() => {
    if (!parsed) {
      setEditAllowed(null);
      setViewSourceAllowed(null);
      return () => {};
    }

    setEditAllowed(null);
    setViewSourceAllowed(null);

    const backendBase = config.getString('backend.baseUrl').replace(/\/$/, '');
    const batchUrl = `${backendBase}/api/catalog-edit-check/batch`;
    const noStoreInit: RequestInit = {
      credentials: 'include',
      cache: 'no-store',
    };

    let cancelled = false;

    const entityRef = parsed.entityRef;

    if (gatesViewSource) {
      fetchApi
        .fetch(batchUrl, {
          ...noStoreInit,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            entityRefs: [entityRef],
            checks: ['edit', 'view-source'],
          }),
        })
        .then(async res => {
          if (res.status === 304 || res.status === 204 || !res.ok) {
            return null;
          }
          try {
            return (await res.json()) as {
              results?: Record<
                string,
                { edit?: boolean; viewSource?: boolean }
              >;
            };
          } catch {
            return null;
          }
        })
        .then(body => {
          if (cancelled) return;
          const row = body?.results?.[entityRef];
          setEditAllowed(Boolean(row?.edit));
          setViewSourceAllowed(Boolean(row?.viewSource));
        })
        .catch(() => {
          if (!cancelled) {
            setEditAllowed(false);
            setViewSourceAllowed(false);
          }
        });
    } else {
      setViewSourceAllowed(true);
      fetchApi
        .fetch(batchUrl, {
          ...noStoreInit,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            entityRefs: [entityRef],
            check: 'edit',
          }),
        })
        .then(async res => {
          if (res.status === 304 || res.status === 204 || !res.ok) {
            return null;
          }
          try {
            return (await res.json()) as Record<string, boolean>;
          } catch {
            return null;
          }
        })
        .then(body => {
          if (!cancelled) {
            setEditAllowed(Boolean(body?.[entityRef]));
          }
        })
        .catch(() => {
          if (!cancelled) {
            setEditAllowed(false);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [parsed, gatesViewSource, fetchApi, config]);

  const value = useMemo((): CatalogEntityPagePermissionsValue => {
    if (!parsed) {
      return defaultValue;
    }
    return {
      onEntityPage: true,
      entityRef: parsed.entityRef,
      kind: parsed.kind,
      gatesViewSource,
      editAllowed,
      viewSourceAllowed,
    };
  }, [parsed, gatesViewSource, editAllowed, viewSourceAllowed]);

  return (
    <CatalogEntityPagePermissionsContext.Provider value={value}>
      {children}
    </CatalogEntityPagePermissionsContext.Provider>
  );
}
